import Phaser from 'phaser';

// Game Hub URL - change this to your actual deployed hub URL
const HUB_URL = 'https://www.dreamdealer.dev';

export default class TitleScene extends Phaser.Scene {
    constructor() {
        super({ key: 'TitleScene' });
        this.selectedIndex = 0;
        this.menuItems = [];
    }

    create() {
        const width = this.scale.width;
        const height = this.scale.height;
        
        // Background
        this.cameras.main.setBackgroundColor('#2d1b4e');
        
        // Create animated background pattern
        this.createBackground();
        
        // Title
        this.titleText = this.add.text(width / 2, height * 0.2, 'WARIO CLONE', {
            fontSize: Math.min(width * 0.1, 72) + 'px',
            fontFamily: 'Arial Black, Impact',
            color: '#ffd700',
            stroke: '#000000',
            strokeThickness: 8,
            shadow: {
                offsetX: 4,
                offsetY: 4,
                color: '#000',
                blur: 8,
                fill: true
            }
        }).setOrigin(0.5);
        
        // Subtitle
        this.subtitleText = this.add.text(width / 2, height * 0.32, 'MICROGAMES', {
            fontSize: Math.min(width * 0.04, 28) + 'px',
            fontFamily: 'Arial',
            color: '#ffffff',
            stroke: '#000000',
            strokeThickness: 3
        }).setOrigin(0.5);

        // Check for saved game
        const savedState = localStorage.getItem('warioCloneGameState');

        // Menu options
        const menuOptions = ['START', 'CONTINUE', 'GAME HUB'];
        const menuY = height * 0.5;
        const menuSpacing = Math.min(height * 0.1, 60);
        const fontSize = Math.min(width * 0.05, 32);

        menuOptions.forEach((option, index) => {
            const y = menuY + (index * menuSpacing);
            const isHub = option === 'GAME HUB';
            const isDisabled = option === 'CONTINUE' && !savedState;
            
            const menuItem = this.add.text(width / 2, y, option, {
                fontSize: fontSize + 'px',
                fontFamily: 'Arial Black',
                color: isHub ? '#4fc3f7' : (isDisabled ? '#666666' : '#ffffff'),
                stroke: '#000000',
                strokeThickness: 4
            }).setOrigin(0.5).setInteractive();
            
            if (!isDisabled) {
                menuItem.on('pointerdown', () => {
                    this.selectedIndex = index;
                    this.updateSelection();
                    this.handleSelection(index);
                });
                
                menuItem.on('pointerover', () => {
                    this.selectedIndex = index;
                    this.updateSelection();
                });
            }
            
            menuItem.setData('isDisabled', isDisabled);
            menuItem.setData('isHub', isHub);
            this.menuItems.push(menuItem);
        });

        // Store save state
        this.savedState = savedState;

        // Controls hint
        this.controlsHint = this.add.text(width / 2, height * 0.85, 
            'D-pad / Arrows to navigate • A / Enter to select • B / Esc = Hub', {
            fontSize: Math.min(width * 0.025, 14) + 'px',
            fontFamily: 'Arial',
            color: 'rgba(255,255,255,0.5)'
        }).setOrigin(0.5);

        // Set up keyboard controls
        this.wasdKeys = this.input.keyboard.addKeys({
            up: Phaser.Input.Keyboard.KeyCodes.W,
            down: Phaser.Input.Keyboard.KeyCodes.S,
            enter: Phaser.Input.Keyboard.KeyCodes.ENTER,
            escape: Phaser.Input.Keyboard.KeyCodes.ESC,
            space: Phaser.Input.Keyboard.KeyCodes.SPACE
        });

        // Arrow keys
        this.cursors = this.input.keyboard.createCursorKeys();

        // Initial selection
        this.updateSelection();

        // Handle resize
        this.scale.on('resize', this.handleResize, this);
    }

    createBackground() {
        // Create a tiled purple pattern
        const graphics = this.make.graphics({ x: 0, y: 0, add: false });
        graphics.fillStyle(0x3d2b5e, 1);
        graphics.fillRect(0, 0, 64, 64);
        graphics.lineStyle(2, 0x4d3b6e, 0.5);
        graphics.strokeRect(0, 0, 64, 64);
        graphics.lineBetween(0, 0, 64, 64);
        graphics.lineBetween(64, 0, 0, 64);
        graphics.generateTexture('title-bg', 64, 64);
        graphics.destroy();
        
        this.bg = this.add.tileSprite(0, 0, this.scale.width * 2, this.scale.height * 2, 'title-bg')
            .setOrigin(0, 0)
            .setScrollFactor(0);
    }

    update() {
        // Animate background
        if (this.bg) {
            this.bg.tilePositionX += 0.5;
            this.bg.tilePositionY += 0.3;
        }

        // Hub controls state — Layer 1 pollable API (touch D-pad + gamepad)
        const hasHub = typeof HubControls !== 'undefined';
        const hubUp = hasHub && HubControls.justPressedDirection('up');
        const hubDown = hasHub && HubControls.justPressedDirection('down');
        const hubConfirm = hasHub && (HubControls.justPressed('a') || HubControls.justPressed('start'));
        const hubCancel = hasHub && HubControls.justPressed('b');

        // Keyboard + hub navigation (Layer 2 — game owns keyboard)
        if (Phaser.Input.Keyboard.JustDown(this.wasdKeys.up) ||
            Phaser.Input.Keyboard.JustDown(this.cursors.up) ||
            hubUp) {
            this.moveSelection(-1);
        }

        if (Phaser.Input.Keyboard.JustDown(this.wasdKeys.down) ||
            Phaser.Input.Keyboard.JustDown(this.cursors.down) ||
            hubDown) {
            this.moveSelection(1);
        }

        // Confirm selection
        if (Phaser.Input.Keyboard.JustDown(this.wasdKeys.enter) ||
            Phaser.Input.Keyboard.JustDown(this.wasdKeys.space) ||
            hubConfirm) {
            this.handleSelection(this.selectedIndex);
        }

        // Go to Hub
        if (Phaser.Input.Keyboard.JustDown(this.wasdKeys.escape) ||
            hubCancel) {
            this.goToHub();
        }
    }

    moveSelection(delta) {
        this.selectedIndex += delta;
        
        // Wrap around
        if (this.selectedIndex < 0) {
            this.selectedIndex = this.menuItems.length - 1;
        } else if (this.selectedIndex >= this.menuItems.length) {
            this.selectedIndex = 0;
        }
        
        this.updateSelection();
    }

    updateSelection() {
        this.menuItems.forEach((item, index) => {
            const isHub = item.getData('isHub');
            const isDisabled = item.getData('isDisabled');
            
            if (index === this.selectedIndex) {
                if (isHub) {
                    item.setColor('#4fc3f7');
                } else if (isDisabled) {
                    item.setColor('#666666');
                } else {
                    item.setColor('#ffd700'); // Gold for selected
                }
                item.setScale(1.15);
            } else {
                if (isHub) {
                    item.setColor('#4fc3f7');
                } else if (isDisabled) {
                    item.setColor('#666666');
                } else {
                    item.setColor('#ffffff');
                }
                item.setScale(1);
            }
        });
    }

    handleSelection(index) {
        const item = this.menuItems[index];
        if (item.getData('isDisabled')) return;
        
        if (index === 0) {
            // Start new game
            localStorage.removeItem('warioCloneGameState');
            this.scene.start('GameScene');
        } else if (index === 1 && this.savedState) {
            // Continue
            this.scene.start('GameScene', { loadSave: true });
        } else if (index === 2) {
            // Game Hub
            this.goToHub();
        }
    }
    
    goToHub() {
        console.log('🎮 Navigating to Game Hub');
        window.location.href = HUB_URL;
    }

    handleResize(gameSize) {
        const width = gameSize.width;
        const height = gameSize.height;
        
        if (this.bg) {
            this.bg.setSize(width * 2, height * 2);
        }
        
        if (this.titleText) {
            this.titleText.setPosition(width / 2, height * 0.2);
            this.titleText.setFontSize(Math.min(width * 0.1, 72));
        }
        
        if (this.subtitleText) {
            this.subtitleText.setPosition(width / 2, height * 0.32);
        }
        
        const menuY = height * 0.5;
        const menuSpacing = Math.min(height * 0.1, 60);
        this.menuItems.forEach((item, index) => {
            item.setPosition(width / 2, menuY + (index * menuSpacing));
        });
        
        if (this.controlsHint) {
            this.controlsHint.setPosition(width / 2, height * 0.85);
        }
    }

}
